import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Wallet as WalletIcon } from "lucide-react";
import SuccessModal from "@/components/SuccessModal";
import EmptyState from "@/components/EmptyState";
import LoadingDots from "@/components/LoadingDots";
import {
  listTransactions,
  topupInit,
  verifyReference,
} from "@/lib/afritask";

const TIERS = [
  { amount: 1000, points: 100, bonus: null },
  { amount: 5000, points: 550, bonus: "+10%" },
  { amount: 10000, points: 1200, bonus: "+20%" },
  { amount: 25000, points: 3250, bonus: "+30%" },
];

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function Wallet() {
  const { wallet, refreshWallet } = useOutletContext();
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);
  const [successModal, setSuccessModal] = useState(null); // {amount, points}

  // Guard: guarantee the Paystack-return verify effect fires EXACTLY ONCE
  // per reference. Prevents React 18 strict-mode double-mount from firing
  // verify twice and prevents accidental re-fires from a re-render.
  const verifiedRefs = useRef(new Set());

  const loadTxs = useCallback(async () => {
    try {
      setTxs(await listTransactions());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadTxs();
  }, [loadTxs]);

  // Handle Paystack return. Runs once per mount; guarded against re-fire.
  useEffect(() => {
    const ref = searchParams.get("reference") || searchParams.get("trxref");
    if (!ref) return;
    if (verifiedRefs.current.has(ref)) return;
    verifiedRefs.current.add(ref);

    // Strip the query param IMMEDIATELY (without React re-render) so a
    // reload / back-forward navigation can't retrigger the verify flow.
    try {
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    } catch { /* ignore */ }

    let cancelled = false;
    setVerifying(true);

    (async () => {
      try {
        const res = await verifyReference(ref);
        if (res.status === "success") {
          const credited = res.transaction?.points_to_credit;
          const amount = res.transaction?.amount_ngn;
          if (res.already_processed) {
            toast.success("Payment already credited");
          } else if (amount && credited) {
            // Trigger the celebration modal instead of a plain toast
            setSuccessModal({ amount, points: credited });
          } else {
            toast.success("Payment successful — points credited");
          }
        } else {
          toast.error(`Payment ${res.status}`);
        }
      } catch (e) {
        console.error(e);
        // Timeout / network errors are transient — Paystack webhook will
        // still credit. Show a soft message, not a scary "failed" error.
        if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
          toast.message("Payment processing — refresh in a moment to see your points");
        } else {
          toast.error(e?.response?.data?.detail || "Could not verify — try refreshing in a moment");
        }
      } finally {
        setVerifying(false);
        // Kick off refresh in the background — do NOT await. If either call
        // is slow, the UI must remain responsive.
        refreshWallet?.().catch(() => {});
        loadTxs().catch(() => {});
      }
    })();

    // Cleanup left intentionally empty — the useRef `verifiedRefs` guard
    // above already guarantees the effect body runs exactly once per ref.
    // A `cancelled` flag here would short-circuit the finally block on React
    // 18 StrictMode's dev-mode unmount/remount cycle, leaving `verifying`
    // stuck true and the success toast never firing.
    return () => { cancelled = true; }; // no-op; kept to satisfy eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTopup = async (amount) => {
    try {
      setBusy(amount);
      const { authorization_url } = await topupInit(amount);
      window.location.href = authorization_url;
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail || "Failed to start Paystack checkout"
      );
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Balance card */}
      <Card className="overflow-hidden">
        <div
          className="p-6 text-white"
          style={{
            background:
              "linear-gradient(135deg, #B45309 0%, #D97706 100%)",
          }}
        >
          <div className="flex items-center gap-2 text-amber-100">
            <WalletIcon className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">
              Work Points balance
            </span>
          </div>
          <p
            className="mt-3 text-5xl font-bold tabular-nums"
            data-testid="wallet-balance"
          >
            {wallet ? wallet.work_points.toLocaleString() : "—"}
          </p>
          <p className="mt-2 text-sm text-amber-100">
            {verifying ? (
              "Verifying last payment…"
            ) : (
              <>
                Lifetime earned:{" "}
                <span className="font-semibold text-white">
                  {wallet ? fmtNaira(wallet.lifetime_earned) : "—"}
                </span>
              </>
            )}
          </p>
        </div>
      </Card>

      {/* Top-up tiers */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Top up</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((t) => (
            <Card key={t.amount} className="transition hover:shadow-md">
              <CardContent className="flex flex-col gap-3 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold tracking-tight">
                    {fmtNaira(t.amount)}
                  </p>
                  {t.bonus && (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      {t.bonus}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-neutral-500">
                  Credits{" "}
                  <span className="font-semibold text-amber-700">
                    {t.points.toLocaleString()} pts
                  </span>
                </p>
                <Button
                  onClick={() => handleTopup(t.amount)}
                  disabled={busy === t.amount}
                  className="w-full rounded-lg bg-amber-700 hover:bg-amber-800"
                  data-testid={`wallet-topup-${t.amount}`}
                >
                  {busy === t.amount
                    ? <LoadingDots label="Redirecting" className="text-white" />
                    : `Top up ${fmtNaira(t.amount)}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Transactions */}
      <section>
        <h2 className="mb-3 text-base font-semibold">Recent transactions</h2>
        <Card>
          <CardContent className="p-0">
            <Table data-testid="wallet-transactions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-6">
                      <EmptyState
                        art="wallet"
                        title="No transactions yet"
                        body="Top up your Work Points to start entering task rounds."
                        testid="empty-transactions"
                      />
                    </TableCell>
                  </TableRow>
                )}
                {txs.map((tx) => (
                  <TableRow key={tx.paystack_reference}>
                    <TableCell className="font-mono text-xs">
                      {tx.paystack_reference}
                    </TableCell>
                    <TableCell>{fmtNaira(tx.amount_ngn)}</TableCell>
                    <TableCell className="font-semibold text-amber-700">
                      {tx.points_to_credit.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          tx.status === "success"
                            ? "default"
                            : tx.status === "failed"
                              ? "destructive"
                              : "secondary"
                        }
                        className={
                          tx.status === "success"
                            ? "bg-emerald-600 hover:bg-emerald-600"
                            : ""
                        }
                      >
                        {tx.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-neutral-500">
                      {fmtWhen(tx.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      <SuccessModal
        open={!!successModal}
        onOpenChange={(v) => !v && setSuccessModal(null)}
        variant="success"
        title="Top-up successful!"
        body={successModal ? `${fmtNaira(successModal.amount)} added · ${successModal.points.toLocaleString()} pts credited` : ""}
        ctaLabel="See your wallet"
        testid="topup-success-modal"
      />
    </div>
  );
}
